"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from "react";
import { animate, motion, useInView, useMotionValue, useReducedMotion, useTransform } from "framer-motion";
interface CountUpValueProps {
  value: string;
  /** false prints the value as written — for figures a roll-up would mangle. */
  count?: boolean;
  className?: string;
}

// Split a stat value into a countable number + its prefix/suffix. Non-numeric
// values (∞, Global) fall through and are rendered verbatim.
const parseValue = (raw: string) => {
  const match = raw.match(/^(\D*)(\d[\d,]*(?:\.\d+)?)(.*)$/);
  if (!match) return null;
  const numStr = match[2].replace(/,/g, "");
  const decimals = (numStr.split(".")[1] ?? "").length;
  return {
    prefix: match[1],
    target: parseFloat(numStr),
    decimals,
    suffix: match[3]
  };
};

// Counts from 0 → value when it scrolls into view.
const CountUpValue = t0 => {
  const $ = _c(22);
  const {
    value,
    count: t1,
    className
  } = t0;
  const count = t1 === undefined ? true : t1;
  let t2;
  if ($[0] !== count || $[1] !== value) {
    t2 = count ? parseValue(value) : null;
    $[0] = count;
    $[1] = value;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const parsed = t2;
  const reduce = useReducedMotion();
  const ref = useRef(null);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      once: true,
      amount: 0.6
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const inView = useInView(ref, t3);
  const mv = useMotionValue(0);
  let t4;
  if ($[4] !== parsed?.decimals) {
    t4 = v => v.toFixed(parsed?.decimals ?? 0);
    $[4] = parsed?.decimals;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const text = useTransform(mv, t4);
  let t5;
  let t6;
  if ($[6] !== inView || $[7] !== mv || $[8] !== parsed || $[9] !== reduce) {
    t5 = () => {
      if (!parsed || !inView || reduce) {
        return;
      }
      const controls = animate(mv, parsed.target, {
        duration: 1.6,
        ease: [0.22, 1, 0.36, 1]
      });
      return () => controls.stop();
    };
    t6 = [inView, parsed, reduce, mv];
    $[6] = inView;
    $[7] = mv;
    $[8] = parsed;
    $[9] = reduce;
    $[10] = t5;
    $[11] = t6;
  } else {
    t5 = $[10];
    t6 = $[11];
  }
  useEffect(t5, t6);
  if (!parsed || reduce) {
    let t7;
    if ($[12] !== className || $[13] !== value) {
      t7 = <span ref={ref} className={className}>{value}</span>;
      $[12] = className;
      $[13] = value;
      $[14] = t7;
    } else {
      t7 = $[14];
    }
    return t7;
  }
  let t7;
  if ($[15] !== text) {
    t7 = <motion.span>{text}</motion.span>;
    $[15] = text;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== className || $[18] !== parsed.prefix || $[19] !== parsed.suffix || $[20] !== t7) {
    t8 = <span ref={ref} className={className}>{parsed.prefix}{t7}{parsed.suffix}</span>;
    $[17] = className;
    $[18] = parsed.prefix;
    $[19] = parsed.suffix;
    $[20] = t7;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  return t8;
};
export default CountUpValue;